import { CountryFlag } from '@/components/country-flag';
import { Icon } from '@/components/icon';
import { Text } from '@/components/text';
import { SHIPMENT_STATUS } from '@/lib/constants';
import { ShipmentJourneyTimelineItem } from '@/lib/models/shipment/types';
import { ShipmentJourneyModel } from '@/lib/models/shipment-journey/types';
import { transformShipmentJourneyToShipmentJourneyTimelineItem } from '@/lib/transformers/shipment';
import { extractCountryCodeFromLocode } from '@/utils/locations';
import { tv } from '@/utils/styles';

export interface ShipmentJourneyProps {
  journey?: ShipmentJourneyModel;
  oldJourney?: ShipmentJourneyTimelineItem[]; // @TODO remove once all shipments have been updated or expired
}

const ShipmentJourney = ({ journey, oldJourney }: ShipmentJourneyProps) => {
  const journeyTimeline = transformShipmentJourneyToShipmentJourneyTimelineItem(journey) || oldJourney;

  if (!journeyTimeline) return null;

  const {
    base,
    progressWrapper,
    progressBarWrapper,
    eventMarker,
    eventIcon,
    eventLabel,
    progressBar,
    progressMarker,
    progress,
    fromToLocation,
    portText,
    locationText,
    journeyBars,
  } = styles();

  const origin = journeyTimeline[0];
  const destination = journeyTimeline[journeyTimeline.length - 1];

  return (
    <div className={base()}>
      <div>
        <div className={fromToLocation()}>
          <CountryFlag countryCode={origin?.port?.country || extractCountryCodeFromLocode(origin?.locationCode)} />
          <Text className={portText()}>{origin?.locationCode || 'N/A'}</Text>
        </div>
        <Text variant="secondary" className={locationText()}>
          {origin?.locationName}
        </Text>
      </div>
      <ul className={journeyBars()}>
        {journeyTimeline.map((journeyItem, i) => {
          const isOrigin = i === 0;
          const isDestination = i === journeyTimeline.length - 1;
          const isOriginOrDestination = isOrigin || isDestination;
          const isInProgress = journeyItem.status === SHIPMENT_STATUS.IN_PROGRESS;
          const isCompleted = journeyItem.status === SHIPMENT_STATUS.COMPLETED;
          const isActive = isCompleted || isInProgress;
          const markerName = isDestination ? 'map-pin' : journeyItem.mode;

          return (
            <li key={i} className={progressWrapper({ isDestination })}>
              <div className={progressBarWrapper()}>
                <div className={eventMarker({ isActive })}>
                  <Icon className={eventIcon({ isActive })} name={markerName} />
                </div>
                <div className={progressBar({ isDestination })}>
                  {/* Horizontal bar on > md breakpoint */}
                  <div
                    className={progress({ className: 'hidden md:block' })}
                    style={{ width: journeyItem.progressPercent }}
                  />
                  {/* Vertical bar on < md breakpoint */}
                  <div
                    className={progress({ className: 'md:hidden' })}
                    style={{ height: journeyItem.progressPercent }}
                  />
                  {isInProgress && <div className={progressMarker()} />}
                </div>
              </div>
              <Text variant="sm" className={eventLabel({ className: isOriginOrDestination ? 'block md:hidden' : '' })}>
                {journeyItem.locationName} {journeyItem.locationCode ? `(${journeyItem.locationCode})` : ''}
              </Text>
            </li>
          );
        })}
      </ul>
      <div className="md:text-right">
        <div className={fromToLocation({ className: 'md:flex-row-reverse' })}>
          <CountryFlag
            countryCode={destination?.port?.country || extractCountryCodeFromLocode(destination?.locationCode)}
          />
          <Text className={portText()}>{destination?.locationCode || 'N/A'}</Text>
        </div>
        <Text variant="secondary" className={locationText()}>
          {destination?.locationName}
        </Text>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative flex w-full max-lg:flex-col md:justify-between md:border-b md:border-dashed md:border-grey-200 md:pb-3',
    journeyBars:
      'mt-2 flex flex-grow max-md:my-4 max-md:flex-col md:absolute md:left-28 md:right-28 md:top-px md:mt-0 md:space-x-1',
    progressWrapper: 'relative last:pb-0 max-md:flex max-md:pb-4',
    progressBarWrapper: 'flex md:mb-2 md:items-center md:space-x-1',
    progressBar:
      'left-[11px] top-4 flex-grow bg-border-grey max-md:absolute max-md:bottom-0 max-md:w-px md:left-0 md:top-1.5 md:h-0.5',
    progress: 'relative bg-green-300 md:h-full',
    progressMarker: 'absolute left-1/2 top-2 ml-2 hidden size-2 rounded-full bg-green-300 md:block',
    eventMarker:
      'z-4 grid size-6 place-items-center rounded-full border border-dashed border-grey-350 bg-white max-md:mr-2',
    eventIcon:
      'block grid size-5 place-items-center rounded-full bg-grey-50 [&_svg]:h-[14px] [&_svg]:w-[14px] [&_svg]:fill-grey-600',
    eventLabel: 'mb-0 md:absolute md:left-0 md:top-7 md:ml-3 md:-translate-x-1/2',
    locationText: '!m-0 max-w-xs truncate font-medium',
    portText: '!m-0 font-bold',
    fromToLocation: 'mb-1 flex items-center gap-2',
  },
  variants: {
    isActive: {
      true: {
        progressBar: 'bg-green-300',
        eventMarker: 'border-solid border-green-300',
        eventIcon: 'bg-green-300 [&_svg]:fill-white',
      },
    },
    isDestination: {
      true: {
        progressBar: 'hidden',
        locationText: 'right-0 md:absolute',
      },
      false: {
        progressWrapper: 'w-full',
      },
    },
  },
});

export { ShipmentJourney };
